/**
 * Kad je odluka „odakle se roba diže" spremna za potvrdu.
 *
 * Izdvojeno iz ekrana zbog jednog slučaja koji je zaključavao rad: stavka koje
 * nema nigde u firmi ne dobija padajuću listu, pa se za nju ne može izabrati
 * mesto ni kad poslovođa hoće. Dok se tražilo da SVE stavke imaju izabrano
 * mesto, jedna takva je držala ceo dokument — poslovođa je gledao pet mesta
 * koja zna i dugme koje ne radi.
 *
 * Traži se ono što se može izabrati, i bar jedno. Stavka bez zalihe se rešava
 * svojim putem („naručeno je, stiže"), ne kroz ovo dugme.
 */
export interface StavkaZaPotvrdu {
  /** Mesta sa kojih se stavka može podići. Prazno = nema je nigde. */
  sources: unknown[];
}

/**
 * Stavke koje jos cekaju mesto.
 *
 * Postoji da bi ekran umeo da KAZE sta mu fali. Sivo dugme bez recenice je
 * zagonetka: covek gleda pet padajucih listi i ne zna koja ga drzi.
 */
export function bezIzabranogMesta<T extends StavkaZaPotvrdu>(
  stavke: T[],
  izabrano: (stavka: T) => string
): T[] {
  return stavke.filter((s) => s.sources.length > 0 && izabrano(s) === '');
}

export function spremnoZaPotvrdu<T extends StavkaZaPotvrdu>(
  stavke: T[],
  izabrano: (stavka: T) => string
): boolean {
  const naIzbor = stavke.filter((s) => s.sources.length > 0);
  return naIzbor.length > 0 && bezIzabranogMesta(stavke, izabrano).length === 0;
}

/**
 * Da li je na ekranu isto ono sto je vec sacuvano.
 *
 * Postoji zbog jedne prijave iz rada: poslovodja pritisne „Potvrdi odakle
 * ide", dugme se za tren ugasi pa opet upali, kartica ostane ista — i covek ne
 * zna da li je klik uopste uhvacen. Odluka se JESTE upisivala, samo se to
 * nigde nije videlo.
 *
 * Kad nema nicega novog, dugme nema sta da uradi: pise „Potvrdjeno" i stoji
 * ugaseno dok covek ne promeni neko mesto.
 */
export function nemaNovihIzmena<T extends StavkaZaPotvrdu>(
  stavke: T[],
  izabrano: (stavka: T) => string,
  sacuvano: (stavka: T) => string | null | undefined
): boolean {
  const naIzbor = stavke.filter((s) => s.sources.length > 0);

  return naIzbor.length > 0
    && naIzbor.every((s) => {
      const staro = sacuvano(s);
      return staro != null && staro !== '' && staro === izabrano(s);
    });
}

/** Stavka koliko treba da se zna koje mesto ponuditi kao podrazumevano. */
export interface StavkaSaPredlogom {
  sources: { code: string }[];
  decidedSource?: string | null;
  proposedSource?: string | null;
  expected?: { arrived?: boolean } | null;
}

/**
 * Koje mesto stoji u padajućoj listi pre nego što poslovođa išta dirne.
 *
 * Redosled je redosled poverenja: njegova odluka; pa mesto na koje je STIGLA
 * roba koju je sam naručio; pa trgovčev upis sa računa.
 *
 * Zašto obećanje ide ispred trgovca (analiza 2.0, I7, 14.10.2026): poslovođa
 * kaže „ne diži, naručeno je, stiže kod mene", roba stigne — a dugme bi
 * poslalo TRGOVČEVO mesto, ono od koga je poslovođa upravo odustao. Stiglo
 * obećanje je novija reč od upisa na računu.
 */
export function podrazumevanoMesto(stavka: StavkaSaPredlogom): string {
  if (stavka.decidedSource) return stavka.decidedSource;
  if (stavka.expected?.arrived && stavka.sources.length > 0) return stavka.sources[0].code;
  return stavka.proposedSource ?? '';
}

/** Isporuka koliko brojka gore treba da zna o njoj. */
export interface IsporukaZaBrojanje {
  items: {
    sources: unknown[];
    decidedSource?: string | null;
    /** Poslovođa je već rekao „naručeno, stiže" — tada stavka nije problem. */
    expected?: unknown | null;
  }[];
}

/**
 * Ceka li ova isporuka da neko kaze odakle roba ide.
 *
 * Panel prikazuje i one o kojima je vec odluceno — da se odluka vidi i po
 * potrebi promeni — ali brojka gore sme da broji samo posao koji STVARNO ceka.
 * Inace poslovodji pise „3 isporuke cekaju“ nad ekranom na kom je sve
 * potvrdjeno, pa prestane da veruje brojci.
 *
 * Stavka koje nema nigde se ne racuna: za nju se mesto ne moze ni izabrati.
 */
export function cekaIzvor(isporuka: IsporukaZaBrojanje): boolean {
  return isporuka.items.some((i) => i.sources.length > 0 && !i.decidedSource);
}

/**
 * Ima li isporuka stavku koje NEMA NIGDE, a niko još nije rekao da stiže.
 *
 * ZAŠTO POSTOJI ODVOJENO OD `cekaIzvor`
 *   To dvoje su različit posao, a dugo su se brojali kao jedno — pa se jedno
 *   od njih nije brojalo uopšte. `cekaIzvor` broji ono gde se mesto MOŽE
 *   izabrati; stavka bez zalihe nema padajuću listu, pa u tu brojku ne ulazi.
 *
 *   Posledica, prijavljena sa terena 15.09.2026 (otpremnica 002-1-5700):
 *   poslovođi je odeljak „Odakle roba ide" stajao sklopljen, bez brojke i sa
 *   natpisom „sve potvrđeno" — iznad isporuke koju niko ne može da reši.
 *   Ekran je tvrdio suprotno od istine.
 *
 * ŠTA SE SA TIM RADI
 *   Ne bira se mesto — nema ga. Poslovođa kaže „naručeno je, stiže", i od tada
 *   stavka više nije ovde: obećanje je odgovor.
 */
export function nemaIzvora(isporuka: IsporukaZaBrojanje): boolean {
  return isporuka.items.some((i) => i.sources.length === 0 && !i.expected);
}
